import React, { useState, useEffect } from 'react';
import { Repeat1, ChevronLeft, ChevronRight, Shuffle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from "../context/UserContext";
import ImageItem from './ImageItem';
import YouTubeViewer from '../components/YouTubeViewer';

const VideoGallery = () => {
  const { user, setUser, authService } = useUser();
  const [videoList, setVideoList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [repeating1, setRepeating1] = useState(user.repeating1);
  const [shuffling, setShuffling] = useState(user.shuffling);

  const getVideoList = async () => {
    if (user.playlist) {
      setVideoList(user.playlist);
      return;
    }

    try {
      // const response = await axios.get(`${config.apiBaseUrl}/video-list`, {
      const response = await authService.get(`/video-list`, {
        params: {
            user_id: user.user_id,
        }
      });
      // console.log(response);
      // console.log(videoList, response.data);
      setVideoList(response.data);

    } catch {
      toast.error("Failed to list downloaded videos")
    }
  };

  useEffect(() => {
    getVideoList();
  }, []);

  const handleRepeat1 = () => {
    if (!repeating1) {
      setShuffling(false);
    }
    setRepeating1(!repeating1);

    setUser((profile) => ({
        ...profile,
        repeating1: !repeating1,
        shuffling: false,
    }));
  };

  const handleShuffle = () => {
    if (!shuffling) {
      setRepeating1(false);
    }
    setShuffling(!shuffling);

    setUser((profile) => ({
        ...profile,
        repeating1: false,
        shuffling: !shuffling,
    }));
  };

  const nextVideo = () => {
    let offset = 1;

    if (repeating1) {
      offset = 0;
    } else if (shuffling) {
      offset = Math.floor(Math.random() * videoList.length);
      if (offset === 0) {
        offset = 1;
      }
    }

    const idx = (currentIndex + offset) % videoList.length;

    if (idx === currentIndex) {
      setVideoList([]);

      setTimeout(() => {
        getVideoList();
        setCurrentIndex((prev) => (prev + offset) % videoList.length);
      }, 500);

    } else {
      setCurrentIndex((prev) => (prev + offset) % videoList.length);
    }
  };

  const prevVideo = () => {
    let offset = 1;

    if (repeating1) {
      offset = 0;
    } else if (shuffling) {
      offset = Math.floor(Math.random() * videoList.length);
      if (offset === 0) {
        offset = 1;
      }
    }

    const idx = (currentIndex - offset + videoList.length) % videoList.length;

    if (idx === currentIndex) {
      setVideoList([]);

      setTimeout(() => {
        getVideoList();
        setCurrentIndex((prev) => (prev - offset + videoList.length) % videoList.length);
      }, 500);

    } else {
      setCurrentIndex((prev) => (prev - offset + videoList.length) % videoList.length);
    }
  };

  const selectVideo = (index) => {
    setCurrentIndex(index);
  };

  const getVisibleVideos = () => {
    const visible = [];
    for (let i = -2; i <= 2; i++) {
      const index = (currentIndex + i + videoList.length) % videoList.length;
      visible.push({ ...videoList[index], position: i });
    }
    return visible;
  };

  const getImageStyle = (position) => {
    const baseStyle = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
      cursor: position !== 0 ? 'pointer' : 'default',
    };

    switch (position) {
      case 0: // Center
        return {
          ...baseStyle,
          left: '50%',
          transform: 'translateX(-50%) translateY(-50%) scale(1)',
          zIndex: 5,
          opacity: 1,
        };
      case -1: // Left
        return {
          ...baseStyle,
          left: '20%',
          transform: 'translateX(-50%) translateY(-50%) scale(0.75) rotateY(45deg)',
          zIndex: 3,
          opacity: 0.7,
        };
      case 1: // Right
        return {
          ...baseStyle,
          right: '20%',
          transform: 'translateX(50%) translateY(-50%) scale(0.75) rotateY(-45deg)',
          zIndex: 3,
          opacity: 0.7,
        };
      case -2: // Far left
        return {
          ...baseStyle,
          left: '5%',
          transform: 'translateX(-50%) translateY(-50%) scale(0.5) rotateY(65deg)',
          zIndex: 1,
          opacity: 0.4,
        };
      case 2: // Far right
        return {
          ...baseStyle,
          right: '5%',
          transform: 'translateX(50%) translateY(-50%) scale(0.5) rotateY(-65deg)',
          zIndex: 1,
          opacity: 0.4,
        };
      default:
        return {
          ...baseStyle,
          opacity: 0,
          zIndex: 0,
        };
    }
  };

  return (
    <>
    <button
        onClick={handleRepeat1}
        className={`hidden custom-flex w-10 h-10 m-1 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
          repeating1 
          ? 'bg-orange-100' 
          : 'bg-white/10 hover:bg-white/20'
        }`}
    >
        <Repeat1 size={24} />
    </button>
    <button
        onClick={prevVideo}
        className="hidden custom-flex w-10 h-10 m-1 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
    >
        <ChevronLeft size={24} />
    </button>
    <div className="w-[calc(65vh)] overflow-hidden bg-gray-800 rounded-lg">
        {/* Main Gallery */}
        <div className="relative flex h-48 m-4">
            <div>
            {getVisibleVideos().map((item, index) => (
                <div
                    key={`${item.video_id}-${item.position}`}
                    style={getImageStyle(item.position)}
                    onClick={() => item.position !== 0 && selectVideo((currentIndex + item.position + videoList.length) % videoList.length)}
                    className="w-86 h-48"
                >
                    {(item.position === 0) ?
                    <div className="relative rounded-lg overflow-hidden group hover:scale-102">
                        {/* <iframe
                            className='flex h-48 rounded-lg aspect-video object-cover'
                            src={config.videoEmbedUrl.replace("video_id", item.video_id)}
                            // allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allow="autoplay"
                        ></iframe> */}
                        {/* <YouTubePlayer
                            video_id={item.video_id}
                            onVideoEnd={nextVideo}
                        /> */}
                        {/* <YouTube
                            videoId={item.video_id}
                            opts={videoOpts}
                            onEnd={nextVideo}
                        /> */}
                        <YouTubeViewer
                            videoId={item.video_id}
                            onEnd={nextVideo}
                            height={192}
                            width={344}
                        />
                        {(!(item.video_id) || item.video_id === '') ? 
                          <div className="absolute inset-0"></div>
                        : <></>
                        }
                        {/* <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div> */}
                        {/* <div className="absolute inset-0"></div> */}
                    </div>
                    :
                    <div className="relative h-full rounded-xl overflow-hidden shadow-2xl group hover:scale-102">
                        <ImageItem
                            video_id={item.video_id}
                            title={item.title}
                            image_src={item.video_info?.image_src}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    </div>
                    }
                </div>
                ))}
            </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex custom-hidden relative z-10 justify-center gap-4 m-4">
            <button
                onClick={handleRepeat1}
                className={`flex w-10 h-10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
                  repeating1 
                  ? 'bg-orange-100' 
                  : 'bg-white/10 hover:bg-white/20'
                }`}
            >
                <Repeat1 size={24} />
            </button>
            <button
                onClick={prevVideo}
                className="flex w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
            >
                <ChevronLeft size={24} />
            </button>
            <button
                onClick={nextVideo}
                className="flex w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
            >
                <ChevronRight size={24} />
            </button>
            <button
                onClick={handleShuffle}
                className={`flex w-10 h-10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
                  shuffling 
                  ? 'bg-orange-100' 
                  : 'bg-white/10 hover:bg-white/20'
                }`}
            >
                <Shuffle size={24} />
            </button>
        </div>

        {/* Dots indicator */}
        {/* <div className="relative z-10 flex justify-center gap-2 m-4">
            {videoList.map((_, index) => (
            <button
                key={index}
                onClick={() => selectVideo(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                    ? 'bg-orange-400 scale-125'
                    : 'bg-orange-400/40 hover:bg-orange-400/60'
                }`}
            />
            ))}
        </div> */}
    </div>
    <button
        onClick={nextVideo}
        className="hidden custom-flex w-10 h-10 m-1 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
    >
        <ChevronRight size={24} />
    </button>
    <button
        onClick={handleShuffle}
        className={`hidden custom-flex w-10 h-10 m-1 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
          shuffling 
          ? 'bg-orange-100' 
          : 'bg-white/10 hover:bg-white/20'
        }`}
    >
        <Shuffle size={24} />
    </button>
    </>
  );
};

export default VideoGallery;

